import React, { useState, useEffect, useRef } from 'react';

const SideImage = ({ src, alt, side, delay = 0 }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollOffset, setScrollOffset] = useState(0);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current || !isMouseDown) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      setMousePosition({ x, y });
    };

    const handleMouseDown = (e) => {
      if (!containerRef.current) return;
      e.preventDefault();
      setIsMouseDown(true);
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      setMousePosition({ x, y });
    };

    const handleMouseUp = () => {
      setIsMouseDown(false);
      setMousePosition({ x: 0, y: 0 });
    };

    const handleMouseLeave = () => {
      setIsMouseDown(false);
      setMousePosition({ x: 0, y: 0 });
    };

    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementCenter = rect.top + rect.height / 2;
      const scrollProgress = (windowHeight / 2 - elementCenter) / windowHeight;
      setScrollOffset(scrollProgress * 30);
    };

    const handleScrollThrottled = () => {
      requestAnimationFrame(handleScroll);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mousedown', handleMouseDown);
      container.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('scroll', handleScrollThrottled, { passive: true });
      handleScroll();
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mousedown', handleMouseDown);
        container.removeEventListener('mouseleave', handleMouseLeave);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('scroll', handleScrollThrottled);
      }
    };
  }, [isMouseDown]);

  const rotateX = isMouseDown ? mousePosition.y * 3 : 0;
  const rotateY = isMouseDown ? mousePosition.x * 3 : 0;
  const translateZ = isMouseDown ? mousePosition.y * 8 : 0;
  const translateY = scrollOffset;

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = containerRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const timeoutId = setTimeout(() => {
            setIsVisible(true);
          }, delay * 1000);

          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '-50px 0px',
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [delay]);

  return (
    <div
      ref={containerRef}
      className={`side-image side-image-${side}`}
      style={{
        perspective: '1000px',
        transformStyle: 'preserve-3d',
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? 'translateX(0)'
          : `translateX(${side === 'left' ? '-30px' : '30px'})`,
        transition: 'opacity 0.4s ease-out, transform 0.4s ease-out',
        willChange: isVisible ? 'auto' : 'opacity, transform',
      }}
    >
      <div
        ref={imageRef}
        className="side-image-wrapper"
        style={{
          transform: `
            translateY(${translateY}px)
            rotateX(${rotateX}deg)
            rotateY(${rotateY}deg)
            translateZ(${translateZ}px)
          `,
          transition: isMouseDown ? 'transform 0.1s ease-out' : 'transform 0.2s ease-out',
        }}
      >
        <img src={src} alt={alt} />
      </div>
      <div className="side-image-overlay"></div>
    </div>
  );
};

export default SideImage;

